import { useEffect, useState } from 'react';
import { healthApi } from '../../lib/api';
import { ErrorBanner } from '../../components/ErrorBanner';
import { formatQty } from '../../lib/money';
import type { SystemHealth } from '../../lib/types';

function formatBytes(bytes: number): string {
  const units = ['B', 'KB', 'MB', 'GB'];
  let n = bytes;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(n < 10 && i > 0 ? 2 : 0)} ${units[i]}`;
}

function formatUptime(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}m`;
}

function Stat({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) {
  return (
    <div className="border border-[var(--border)] px-3 py-2">
      <p className="text-[10.5px] uppercase tracking-wide text-[var(--text-dim)]">{label}</p>
      <p className="mono text-[16px] leading-tight">{value}</p>
      {hint && <p className="mt-0.5 text-[11px] text-[var(--text-faint)]">{hint}</p>}
    </div>
  );
}

export function SystemHealthPage() {
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    healthApi.system().then(setHealth).catch(setError);
  }, []);

  return (
    <div className="mx-auto max-w-3xl p-4">
      <div className="mb-3 border-b border-[var(--border)] pb-2">
        <h1 className="text-[14px] font-semibold">System health</h1>
        <p className="text-[11.5px] text-[var(--text-faint)]">
          The screen a customer reads before calling support (PRD §5.13). Most support cases should be resolvable from this alone.
        </p>
      </div>

      {error ? <ErrorBanner error={error} onDismiss={() => setError(null)} /> : null}

      {health ? (
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-3 gap-2">
            <Stat label="Version" value={health.version} />
            <Stat label="Build" value={health.build} />
            <Stat label="Schema version" value={health.schemaVersion} />
            <Stat label="Database size" value={formatBytes(health.databaseSizeBytes)} />
            <Stat label="Uptime" value={formatUptime(health.uptimeSeconds)} />
            <Stat label="Site ID" value={health.siteId} />
          </div>

          <div>
            <h2 className="mb-1 text-[11px] font-medium uppercase tracking-wide text-[var(--text-dim)]">Row counts</h2>
            <table className="w-full border-collapse text-[12.5px]">
              <tbody>
                {Object.entries(health.rowCounts).map(([table, count]) => (
                  <tr key={table} className="border-b border-[var(--border)] last:border-b-0">
                    <td className="mono py-1 pr-2 text-[var(--text-dim)]">{table}</td>
                    <td className="num py-1">{formatQty(String(count), 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div>
            <h2 className="mb-1 text-[11px] font-medium uppercase tracking-wide text-[var(--text-dim)]">Compliance & backup</h2>
            <div className="grid grid-cols-2 gap-2">
              <Stat label="Last successful backup" value={health.lastBackupAt ?? '—'} hint="Arrives in a later phase." />
              <Stat label="Compliance data date" value={health.complianceDataDate ?? '—'} hint="Arrives in a later phase." />
            </div>
          </div>
        </div>
      ) : (
        !error && <p className="text-[12.5px] text-[var(--text-dim)]">Loading…</p>
      )}
    </div>
  );
}
